import { CalendarDays, ThermometerSun, ThermometerSnowflake, Droplets } from 'lucide-react';
import { getWmoIcon, getWmoLabel } from '@/lib/wmo-codes';
import { useTranslation } from '@/hooks/useTranslation';

export interface DiaPrevisao {
  max: number;
  min: number;
  chuva: number;
  code: number;
}

export interface Feriado {
  date: string;
  name: string;
  type: 'national' | 'state' | 'municipal' | 'optional';
}

interface FeriadoCardProps {
  feriado: Feriado;
  previsao?: DiaPrevisao;
}

const TYPE_COLOR: Record<Feriado['type'], string> = {
  national: 'bg-teal-50 text-teal-deep',
  state: 'bg-amber-50 text-amber-700',
  municipal: 'bg-blue-50 text-blue-700',
  optional: 'bg-gray-50 text-gray-600',
};

export function FeriadoCard({ feriado, previsao }: FeriadoCardProps) {
  const { t, lang } = useTranslation();

  const dateLabel = new Date(feriado.date + 'T12:00:00').toLocaleDateString(
    lang === 'pt-BR' ? 'pt-BR' : lang,
    { weekday: 'short', day: 'numeric', month: 'short' }
  );
  const wmoIcon = previsao ? getWmoIcon(previsao.code, 'w-5 h-5') : null;
  const wmoLabel = previsao ? getWmoLabel(previsao.code, lang) : null;

  return (
    <div
      className={`flex items-center justify-between rounded-xl border p-3 transition-shadow hover:shadow-sm ${
        previsao ? 'bg-sand-50 border-sand-200' : 'bg-white border-sand-200 opacity-80'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-10 h-10 rounded-lg bg-white border border-sand-200 flex items-center justify-center flex-shrink-0"
          title={wmoLabel ?? undefined}
        >
          {wmoIcon ?? <CalendarDays className="w-5 h-5 text-warm-gray" />}
        </div>
        <div className="min-w-0">
          <p className="font-body text-sm font-semibold text-warm-dark truncate">{feriado.name}</p>
          <p className="font-body text-[11px] text-warm-gray capitalize">
            {dateLabel}
            <span className="mx-1.5 text-sand-300">·</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${TYPE_COLOR[feriado.type]}`}
            >
              {t(`widget.feriados.type.${feriado.type}`)}
            </span>
          </p>
        </div>
      </div>

      <div className="text-right flex-shrink-0 pl-2">
        {previsao ? (
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1 text-warm-gray">
              <ThermometerSun className="w-3.5 h-3.5 text-orange-600" />
              <span className="font-display text-sm">{previsao.max}°</span>
              <span className="text-sand-300">/</span>
              <ThermometerSnowflake className="w-3.5 h-3.5 text-blue-600" />
              <span className="font-display text-sm">{previsao.min}°</span>
            </div>
            <div className="flex items-center gap-1 text-ocean">
              <Droplets className="w-3.5 h-3.5" />
              <span className="font-body text-xs font-medium">{previsao.chuva}%</span>
            </div>
            <div className="w-8 h-8 rounded-lg bg-white border border-sand-200 flex items-center justify-center sm:hidden">
              {wmoIcon}
            </div>
          </div>
        ) : (
          <span className="font-body text-[11px] text-warm-gray bg-sand-100 px-2 py-1 rounded-md">
            {t('widget.feriados.soon')}
          </span>
        )}
      </div>
    </div>
  );
}
